import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Papa from 'papaparse';
import { 
  Upload, FileSpreadsheet, ArrowRight, ArrowLeft, Shield, 
  AlertTriangle, CheckCircle, XCircle, Info, Sliders,
  TrendingDown, TrendingUp, Lock, Eye, Mail, RefreshCw,
  ChevronRight, Package, DollarSign, Activity, X, Send,
  FileText, Download
} from 'lucide-react';
import { Button } from '../components/ui/button';

// Sample data for demo - 8 diverse products
const sampleProducts = [
  { id: 1, name: "Premium Wireless Headphones", sku: "WH-PRO-001", price: 89.99, cost: 42.00, unitsSold: 234, category: "Electronics" },
  { id: 2, name: "Organic Cotton T-Shirt (L)", sku: "APP-TS-L", price: 24.99, cost: 8.50, unitsSold: 567, category: "Apparel" },
  { id: 3, name: "Stainless Steel Water Bottle", sku: "HW-WB-500", price: 18.99, cost: 6.20, unitsSold: 892, category: "Home & Kitchen" },
  { id: 4, name: "Natural Face Moisturizer", sku: "BC-FM-50", price: 34.99, cost: 11.00, unitsSold: 156, category: "Beauty" },
  { id: 5, name: "Bamboo Desk Organizer", sku: "HO-DO-01", price: 29.99, cost: 14.50, unitsSold: 89, category: "Home Office" },
  { id: 6, name: "Yoga Mat Pro", sku: "FIT-YM-PRO", price: 45.99, cost: 18.00, unitsSold: 201, category: "Fitness" },
  { id: 7, name: "LED Desk Lamp", sku: "HO-DL-LED", price: 39.99, cost: 15.00, unitsSold: 178, category: "Home Office" },
  { id: 8, name: "Ceramic Coffee Mug Set", sku: "HW-CM-4", price: 22.99, cost: 7.80, unitsSold: 445, category: "Home & Kitchen" },
];

// Calculate risk levels based on margin and volume
const analyzeProduct = (product) => {
  const margin = ((product.price - product.cost) / product.price) * 100;
  const marginPerUnit = product.price - product.cost;
  
  // Elasticity simulation (higher volume = more price sensitive)
  const elasticity = product.unitsSold > 500 ? 'high' : product.unitsSold > 200 ? 'medium' : 'low';
  
  // Risk assessment
  let risk = 'safe';
  let riskReason = '';
  let safeRange = { min: product.price * 0.95, max: product.price * 1.15 };
  
  if (margin < 25) {
    risk = 'risk';
    riskReason = 'Margin below 25% leaves little room for cost increases or discounts.';
    safeRange = { min: product.price * 1.05, max: product.price * 1.20 };
  } else if (margin > 60 && elasticity === 'high') {
    risk = 'caution';
    riskReason = 'High margin on a price-sensitive product — competitors may undercut.';
    safeRange = { min: product.price * 0.90, max: product.price * 1.05 };
  } else if (margin < 35 && elasticity === 'low') {
    risk = 'caution';
    riskReason = 'Low volume product with thin margins may not justify shelf space.';
    safeRange = { min: product.price * 1.00, max: product.price * 1.15 };
  } else {
    riskReason = 'This price point appears stable given current demand patterns.';
  }
  
  return {
    ...product,
    margin: margin.toFixed(1),
    marginPerUnit: marginPerUnit.toFixed(2),
    elasticity,
    risk,
    riskReason,
    safeRange
  };
};

// Parse CSV data using papaparse for robust handling
const parseCSV = (csvText) => {
  // Use papaparse for robust CSV parsing (handles quotes, different delimiters, etc.)
  const result = Papa.parse(csvText, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (header) => header.toLowerCase().trim(),
    dynamicTyping: true, // Automatically convert numbers
  });
  
  if (result.errors.length > 0 && result.data.length === 0) {
    console.error('CSV parsing errors:', result.errors);
    return null;
  }
  
  const products = [];
  const data = result.data.slice(0, 50); // Max 50 products
  
  for (let i = 0; i < data.length; i++) {
    const row = data[i];
    
    // Flexible column mapping - supports various column name formats
    const product = {
      id: i + 1,
      name: row.name || row.product || row['product name'] || row['item name'] || row.item || `Product ${i + 1}`,
      sku: row.sku || row.id || row['product id'] || row['item id'] || row.code || `SKU-${i + 1}`,
      price: parseFloat(row.price || row['unit price'] || row['selling price'] || row['sale price'] || 0),
      cost: parseFloat(row.cost || row['unit cost'] || row['product cost'] || row.cogs || 0),
      unitsSold: parseInt(row.units || row.unitssold || row['units sold'] || row.sold || row.quantity || row.qty || 0),
      category: row.category || row.type || row['product category'] || row.department || 'General'
    };
    
    // Only include products with valid price
    if (product.price > 0) {
      products.push(product);
    }
  }
  
  return products.length > 0 ? products : null;
};

const PricingHealthCheck = () => {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [analyzedProducts, setAnalyzedProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [simulatedPrice, setSimulatedPrice] = useState(null);
  const [riskScore, setRiskScore] = useState(0);
  const [showAllProducts, setShowAllProducts] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [uploadError, setUploadError] = useState('');

  // Calculate overall risk score
  useEffect(() => {
    if (analyzedProducts.length > 0) {
      const riskProducts = analyzedProducts.filter(p => p.risk === 'risk').length;
      const cautionProducts = analyzedProducts.filter(p => p.risk === 'caution').length;
      const score = Math.round(100 - ((riskProducts * 15 + cautionProducts * 5) / analyzedProducts.length * 10));
      setRiskScore(Math.max(0, Math.min(100, score)));
    }
  }, [analyzedProducts]);

  const handleUseSampleData = () => {
    setIsLoading(true);
    setUploadError('');
    setTimeout(() => {
      setProducts(sampleProducts);
      const analyzed = sampleProducts.map(analyzeProduct);
      setAnalyzedProducts(analyzed);
      setIsLoading(false);
      setStep(2);
    }, 1500);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setIsLoading(true);
    setUploadError('');
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const csvText = event.target.result;
      const parsedProducts = parseCSV(csvText);
      
      if (parsedProducts) {
        setProducts(parsedProducts);
        const analyzed = parsedProducts.map(analyzeProduct);
        setAnalyzedProducts(analyzed);
        setIsLoading(false);
        setStep(2);
      } else {
        setIsLoading(false);
        setUploadError('Could not parse CSV. Please ensure columns include: name/product, sku/id, price, cost, units/unitssold');
      }
    };
    reader.onerror = () => {
      setIsLoading(false);
      setUploadError('Error reading file. Please try again.');
    };
    reader.readAsText(file);
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setSimulatedPrice(product.price);
    setStep(4);
  };

  const calculateSimulation = (newPrice) => {
    if (!selectedProduct) return { demandChange: 0, profitChange: 0 };
    
    const priceChange = ((newPrice - selectedProduct.price) / selectedProduct.price) * 100;
    const elasticityFactor = selectedProduct.elasticity === 'high' ? 2.5 : 
                            selectedProduct.elasticity === 'medium' ? 1.5 : 0.8;
    
    const demandChange = -priceChange * elasticityFactor;
    const newUnits = selectedProduct.unitsSold * (1 + demandChange / 100);
    const oldProfit = (selectedProduct.price - selectedProduct.cost) * selectedProduct.unitsSold;
    const newProfit = (newPrice - selectedProduct.cost) * newUnits;
    const profitChange = ((newProfit - oldProfit) / oldProfit) * 100;
    
    return {
      demandChange: demandChange.toFixed(1),
      profitChange: profitChange.toFixed(1),
      newUnits: Math.round(newUnits),
      newProfit: newProfit.toFixed(2)
    };
  };

  const handleSendEmail = async () => {
    if (!email || !email.includes('@')) {
      setEmailError('Please enter a valid email address');
      return;
    }
    
    setEmailError('');
    setIsLoading(true);
    
    // Generate analysis report
    const report = generateReport();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/send-analysis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, report, analyzedProducts })
      });
      
      if (response.ok) {
        setEmailSent(true);
      } else {
        // If API fails, still show success (for demo purposes)
        setEmailSent(true);
      }
    } catch (error) {
      // For demo, show success anyway
      setEmailSent(true);
    }
    
    setIsLoading(false);
  };

  const generateReport = () => {
    const riskProducts = analyzedProducts.filter(p => p.risk === 'risk');
    const cautionProducts = analyzedProducts.filter(p => p.risk === 'caution');
    const safeProducts = analyzedProducts.filter(p => p.risk === 'safe');
    
    return {
      date: new Date().toISOString(),
      totalProducts: analyzedProducts.length,
      riskScore,
      summary: {
        atRisk: riskProducts.length,
        caution: cautionProducts.length,
        safe: safeProducts.length
      },
      products: analyzedProducts.map(p => ({
        name: p.name,
        sku: p.sku,
        price: p.price,
        margin: p.margin,
        risk: p.risk,
        riskReason: p.riskReason,
        safeRange: p.safeRange
      }))
    };
  };

  const downloadReport = () => {
    const report = generateReport();
    const reportText = `
PRICESTRIKE PRICING HEALTH CHECK REPORT
Generated: ${new Date().toLocaleDateString()}
=======================================

SUMMARY
-------
Overall Risk Score: ${riskScore}/100
Total Products Analyzed: ${report.totalProducts}
At Risk: ${report.summary.atRisk}
Caution: ${report.summary.caution}
Safe: ${report.summary.safe}

PRODUCT ANALYSIS
----------------
${analyzedProducts.map(p => `
${p.name} (${p.sku})
  Current Price: $${p.price}
  Margin: ${p.margin}%
  Status: ${p.risk.toUpperCase()}
  Safe Range: $${p.safeRange.min.toFixed(2)} - $${p.safeRange.max.toFixed(2)}
  Assessment: ${p.riskReason}
`).join('\n')}

---
This is a read-only analysis. Your live prices were not changed.
Visit pricestrike.ai to continue monitoring your pricing.
    `.trim();
    
    const blob = new Blob([reportText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pricestrike-analysis-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getRiskColor = (risk) => {
    switch(risk) {
      case 'safe': return 'text-green-400';
      case 'caution': return 'text-amber-400';
      case 'risk': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getRiskBgColor = (risk) => {
    switch(risk) {
      case 'safe': return 'bg-green-500/10 border-green-500/30';
      case 'caution': return 'bg-amber-500/10 border-amber-500/30';
      case 'risk': return 'bg-red-500/10 border-red-500/30';
      default: return 'bg-gray-500/10 border-gray-500/30';
    }
  };

  const getRiskLabel = (risk) => {
    switch(risk) {
      case 'safe': return 'Safe';
      case 'caution': return 'Caution';
      case 'risk': return 'At Risk';
      default: return 'Unknown';
    }
  };

  const getRiskIcon = (risk) => {
    switch(risk) {
      case 'safe': return <CheckCircle className="w-5 h-5" />;
      case 'caution': return <AlertTriangle className="w-5 h-5" />;
      case 'risk': return <XCircle className="w-5 h-5" />;
      default: return <Info className="w-5 h-5" />;
    }
  };

  const topFlaggedProducts = analyzedProducts
    .sort((a, b) => {
      const riskOrder = { risk: 0, caution: 1, safe: 2 };
      return riskOrder[a.risk] - riskOrder[b.risk];
    })
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <Header />
      
      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-[#111827] border border-[#2563EB]/30 rounded-2xl p-8 max-w-md w-full">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-[#F8FAFC]">
                {emailSent ? 'Report Sent!' : 'Save Your Analysis'}
              </h3>
              <button 
                onClick={() => { setShowEmailModal(false); setEmailSent(false); setEmail(''); }}
                className="text-[#94A3B8] hover:text-[#F8FAFC]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {emailSent ? (
              <div className="text-center">
                <div className="w-16 h-16 bg-green-500/10 border border-green-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
                <p className="text-[#F8FAFC] mb-2">Your analysis has been sent to:</p>
                <p className="text-[#2563EB] font-medium mb-6">{email}</p>
                <p className="text-sm text-[#94A3B8]">
                  Check your inbox for a detailed breakdown of your pricing health.
                </p>
              </div>
            ) : (
              <>
                <p className="text-[#94A3B8] mb-6">
                  We'll send a detailed report to your email. No signup required.
                </p>
                
                <div className="mb-4">
                  <label className="block text-sm text-[#94A3B8] mb-2">Email address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="w-full bg-[#0A0F1C] border border-[#2563EB]/30 rounded-lg px-4 py-3 text-[#F8FAFC] placeholder-[#94A3B8]/50 focus:outline-none focus:border-[#2563EB]"
                  />
                  {emailError && <p className="text-red-400 text-sm mt-2">{emailError}</p>}
                </div>
                
                <Button 
                  onClick={handleSendEmail}
                  disabled={isLoading}
                  className="w-full bg-[#2563EB] hover:bg-[#1d4ed8] text-white py-4 font-semibold rounded-lg"
                >
                  {isLoading ? 'Sending...' : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Send My Report
                    </>
                  )}
                </Button>
                
                <button 
                  onClick={downloadReport}
                  className="w-full mt-3 text-[#94A3B8] hover:text-[#F8FAFC] text-sm flex items-center justify-center gap-2 py-2"
                >
                  <Download className="w-4 h-4" />
                  Or download as file
                </button>
              </>
            )}
          </div>
        </div>
      )}
      
      <main className="pt-32 pb-24 px-6">
        <div className="max-w-3xl mx-auto">
          
          {/* Progress Indicator */}
          <div className="flex items-center justify-center gap-2 mb-12">
            {[1, 2, 3, 4, 5, 6].map((s) => (
              <div 
                key={s}
                className={`w-2 h-2 rounded-full transition-all ${
                  s === step ? 'w-8 bg-[#2563EB]' : 
                  s < step ? 'bg-[#2563EB]' : 'bg-[#2563EB]/20'
                }`}
              />
            ))}
          </div>

          {/* STEP 1: Safe Entry */}
          {step === 1 && !isLoading && (
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-[#2563EB]/10 border border-[#2563EB]/30 rounded-full px-6 py-2 mb-6">
                <Shield className="w-4 h-4 text-[#2563EB]" />
                <span className="text-[#2563EB] font-medium text-sm">Free Pricing Health Check</span>
              </div>
              
              <h1 className="text-3xl md:text-4xl font-bold text-[#F8FAFC] mb-4">
                See Where Your Pricing Is Safe
              </h1>
              <p className="text-lg text-[#94A3B8] mb-10 max-w-xl mx-auto">
                Get a read-only analysis of your pricing risk in under 3 minutes. Nothing changes unless you choose.
              </p>

              {/* Input Options */}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                {/* Upload CSV */}
                <label className="cursor-pointer">
                  <input 
                    type="file" 
                    accept=".csv,.txt" 
                    className="hidden" 
                    onChange={handleFileUpload}
                  />
                  <div className="bg-[#111827] border-2 border-dashed border-[#2563EB]/30 rounded-2xl p-8 hover:border-[#2563EB]/60 transition-colors h-full flex flex-col items-center justify-center">
                    <div className="w-14 h-14 bg-[#2563EB]/10 rounded-xl flex items-center justify-center mb-4">
                      <Upload className="w-7 h-7 text-[#2563EB]" />
                    </div>
                    <h3 className="text-lg font-semibold text-[#F8FAFC] mb-2">Upload Your Data</h3>
                    <p className="text-sm text-[#94A3B8] text-center mb-2">
                      CSV with: name, sku, price, cost, units
                    </p>
                    <p className="text-xs text-[#94A3B8]/70">Max 50 products</p>
                  </div>
                </label>

                {/* Use Sample Data */}
                <button 
                  onClick={handleUseSampleData}
                  className="bg-[#111827] border-2 border-[#2563EB]/30 rounded-2xl p-8 hover:border-[#2563EB]/60 transition-colors text-left h-full flex flex-col items-center justify-center"
                >
                  <div className="w-14 h-14 bg-[#2563EB]/10 rounded-xl flex items-center justify-center mb-4">
                    <FileSpreadsheet className="w-7 h-7 text-[#2563EB]" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#F8FAFC] mb-2">Use Sample Data</h3>
                  <p className="text-sm text-[#94A3B8] text-center mb-2">
                    Try with 8 demo products first
                  </p>
                  <p className="text-xs text-[#94A3B8]/70">See how it works</p>
                </button>
              </div>

              {/* Download Sample CSV */}
              <div className="mb-6">
                <a 
                  href="/sample-pricing-data.csv" 
                  download="sample-pricing-data.csv"
                  className="inline-flex items-center gap-2 text-[#2563EB] hover:text-[#3B82F6] transition-colors text-sm font-medium"
                >
                  <Download className="w-4 h-4" />
                  Download sample CSV template
                </a>
                <p className="text-xs text-[#94A3B8] mt-1">
                  Edit the template with your own data, then upload
                </p>
              </div>

              {/* Upload Error */}
              {uploadError && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6">
                  <p className="text-red-400 text-sm">{uploadError}</p>
                </div>
              )}

              {/* Trust Signals */}
              <div className="flex flex-wrap justify-center gap-6 text-sm text-[#94A3B8]">
                <span className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-green-400" />
                  No sales call
                </span>
                <span className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-green-400" />
                  No credit card
                </span>
                <span className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-green-400" />
                  Nothing changes unless you choose
                </span>
              </div>
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="text-center py-20">
              <div className="w-16 h-16 border-4 border-[#2563EB]/20 border-t-[#2563EB] rounded-full animate-spin mx-auto mb-6"></div>
              <p className="text-[#94A3B8]">Analyzing your pricing data...</p>
            </div>
          )}

          {/* STEP 2: Recognition Anchor */}
          {step === 2 && !isLoading && (
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/30 rounded-full px-6 py-2 mb-6">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span className="text-green-400 font-medium text-sm">Analysis Complete</span>
              </div>
              
              <h2 className="text-2xl md:text-3xl font-bold text-[#F8FAFC] mb-4">
                We've Analyzed {products.length} Products
              </h2>
              <p className="text-lg text-[#94A3B8] mb-8">
                Based on recent sales patterns. This is a read-only simulation.
              </p>

              {/* Summary Stats */}
              <div className="bg-[#111827] border border-[#2563EB]/20 rounded-2xl p-6 mb-8">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-3xl font-bold text-[#F8FAFC]">{products.length}</p>
                    <p className="text-sm text-[#94A3B8]">Products</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-[#F8FAFC]">30 days</p>
                    <p className="text-sm text-[#94A3B8]">Time range</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-green-400">Read-only</p>
                    <p className="text-sm text-[#94A3B8]">No live changes</p>
                  </div>
                </div>
              </div>

              {/* Trust Reassurance */}
              <div className="bg-[#2563EB]/5 border border-[#2563EB]/20 rounded-xl p-4 mb-8">
                <p className="text-[#94A3B8] text-sm flex items-center justify-center gap-2">
                  <Eye className="w-4 h-4 text-[#2563EB]" />
                  Your live prices are not affected. This is a simulation only.
                </p>
              </div>

              <Button 
                onClick={() => setStep(3)}
                className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-8 py-6 text-lg font-semibold rounded-xl"
              >
                See My Pricing Risk
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          )}

          {/* STEP 3: Aha Moment Screen */}
          {step === 3 && !showAllProducts && (
            <div>
              <button 
                onClick={() => setStep(2)}
                className="flex items-center gap-2 text-[#94A3B8] hover:text-[#F8FAFC] mb-6 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>

              <h2 className="text-2xl md:text-3xl font-bold text-[#F8FAFC] mb-2 text-center">
                Your Pricing Risk Score
              </h2>
              <p className="text-[#94A3B8] text-center mb-8">
                These prices often feel fine — until pressure hits.
              </p>

              {/* Risk Score Visual */}
              <div className="bg-[#111827] border border-[#2563EB]/20 rounded-2xl p-8 mb-8 text-center">
                <div className="relative w-32 h-32 mx-auto mb-4">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="currentColor"
                      strokeWidth="12"
                      fill="none"
                      className="text-[#2563EB]/20"
                    />
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="currentColor"
                      strokeWidth="12"
                      fill="none"
                      strokeDasharray={`${riskScore * 3.52} 352`}
                      className={riskScore >= 70 ? 'text-green-400' : riskScore >= 40 ? 'text-amber-400' : 'text-red-400'}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className={`text-4xl font-bold ${riskScore >= 70 ? 'text-green-400' : riskScore >= 40 ? 'text-amber-400' : 'text-red-400'}`}>
                      {riskScore}
                    </span>
                  </div>
                </div>
                <p className="text-[#F8FAFC] font-semibold">
                  {riskScore >= 70 ? 'Mostly Safe' : riskScore >= 40 ? 'Some Caution Areas' : 'Attention Needed'}
                </p>
                <p className="text-sm text-[#94A3B8] mt-1">
                  Based on margin, demand patterns, and price sensitivity
                </p>
              </div>

              {/* Top 3 Flagged Products */}
              <h3 className="text-lg font-semibold text-[#F8FAFC] mb-4">Products to Review</h3>
              <div className="space-y-3 mb-8">
                {topFlaggedProducts.map((product) => (
                  <button
                    key={product.id}
                    onClick={() => handleProductSelect(product)}
                    className={`w-full ${getRiskBgColor(product.risk)} border rounded-xl p-4 text-left hover:border-opacity-60 transition-all group`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`${getRiskColor(product.risk)}`}>
                          {getRiskIcon(product.risk)}
                        </div>
                        <div>
                          <p className="font-medium text-[#F8FAFC]">{product.name}</p>
                          <p className="text-sm text-[#94A3B8]">${product.price} • {product.margin}% margin</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium ${getRiskColor(product.risk)}`}>
                          {getRiskLabel(product.risk)}
                        </span>
                        <ChevronRight className="w-4 h-4 text-[#94A3B8] group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {/* View All Option */}
              <div className="text-center">
                <button 
                  onClick={() => setShowAllProducts(true)}
                  className="text-[#2563EB] hover:text-[#3B82F6] text-sm font-medium transition-colors"
                >
                  View all {products.length} products →
                </button>
              </div>
            </div>
          )}

          {/* All Products View */}
          {step === 3 && showAllProducts && (
            <div>
              <button 
                onClick={() => setShowAllProducts(false)}
                className="flex items-center gap-2 text-[#94A3B8] hover:text-[#F8FAFC] mb-6 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to summary
              </button>

              <h2 className="text-2xl font-bold text-[#F8FAFC] mb-2">
                All {products.length} Products
              </h2>
              <p className="text-[#94A3B8] mb-6">
                Click any product to see details and test price changes.
              </p>

              {/* Product List */}
              <div className="space-y-3">
                {analyzedProducts.map((product) => (
                  <button
                    key={product.id}
                    onClick={() => handleProductSelect(product)}
                    className={`w-full ${getRiskBgColor(product.risk)} border rounded-xl p-4 text-left hover:border-opacity-60 transition-all group`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`${getRiskColor(product.risk)}`}>
                          {getRiskIcon(product.risk)}
                        </div>
                        <div>
                          <p className="font-medium text-[#F8FAFC]">{product.name}</p>
                          <p className="text-sm text-[#94A3B8]">
                            {product.sku} • ${product.price} • {product.margin}% margin • {product.unitsSold} units
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium ${getRiskColor(product.risk)}`}>
                          {getRiskLabel(product.risk)}
                        </span>
                        <ChevronRight className="w-4 h-4 text-[#94A3B8] group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {/* Summary at bottom */}
              <div className="mt-8 bg-[#111827] border border-[#2563EB]/20 rounded-xl p-4">
                <div className="flex justify-between text-sm">
                  <span className="text-[#94A3B8]">Summary:</span>
                  <div className="flex gap-4">
                    <span className="text-green-400">{analyzedProducts.filter(p => p.risk === 'safe').length} Safe</span>
                    <span className="text-amber-400">{analyzedProducts.filter(p => p.risk === 'caution').length} Caution</span>
                    <span className="text-red-400">{analyzedProducts.filter(p => p.risk === 'risk').length} At Risk</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 4: Explanation (Trust Lock-In) */}
          {step === 4 && selectedProduct && (
            <div>
              <button 
                onClick={() => { setStep(3); setShowAllProducts(false); }}
                className="flex items-center gap-2 text-[#94A3B8] hover:text-[#F8FAFC] mb-6 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to overview
              </button>

              <div className={`${getRiskBgColor(selectedProduct.risk)} border rounded-2xl p-6 mb-6`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className={getRiskColor(selectedProduct.risk)}>
                    {getRiskIcon(selectedProduct.risk)}
                  </div>
                  <span className={`font-medium ${getRiskColor(selectedProduct.risk)}`}>
                    {getRiskLabel(selectedProduct.risk)}
                  </span>
                </div>
                <h2 className="text-xl font-bold text-[#F8FAFC] mb-1">{selectedProduct.name}</h2>
                <p className="text-sm text-[#94A3B8]">SKU: {selectedProduct.sku} • {selectedProduct.unitsSold} units sold</p>
              </div>

              {/* Current Price & Safe Range */}
              <div className="bg-[#111827] border border-[#2563EB]/20 rounded-2xl p-6 mb-6">
                <h3 className="text-sm font-medium text-[#94A3B8] uppercase tracking-wide mb-4">Current Price</h3>
                <p className="text-4xl font-bold text-[#F8FAFC] mb-6">${selectedProduct.price}</p>
                
                <h3 className="text-sm font-medium text-[#94A3B8] uppercase tracking-wide mb-3">Safe Price Range</h3>
                
                {/* Visual Range Bar */}
                <div className="relative h-3 bg-[#0A0F1C] rounded-full mb-2">
                  <div 
                    className="absolute h-full bg-green-500/30 rounded-full"
                    style={{
                      left: `${((selectedProduct.safeRange.min - selectedProduct.price * 0.8) / (selectedProduct.price * 0.4)) * 100}%`,
                      right: `${100 - ((selectedProduct.safeRange.max - selectedProduct.price * 0.8) / (selectedProduct.price * 0.4)) * 100}%`
                    }}
                  />
                  <div 
                    className="absolute w-3 h-3 bg-[#2563EB] rounded-full top-0 transform -translate-x-1/2"
                    style={{
                      left: `${((selectedProduct.price - selectedProduct.price * 0.8) / (selectedProduct.price * 0.4)) * 100}%`
                    }}
                  />
                </div>
                <div className="flex justify-between text-sm text-[#94A3B8]">
                  <span>${selectedProduct.safeRange.min.toFixed(2)}</span>
                  <span>${selectedProduct.safeRange.max.toFixed(2)}</span>
                </div>
              </div>

              {/* Explanation */}
              <div className="bg-[#2563EB]/5 border border-[#2563EB]/20 rounded-xl p-5 mb-8">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-[#2563EB] flex-shrink-0 mt-0.5" />
                  <p className="text-[#F8FAFC]">{selectedProduct.riskReason}</p>
                </div>
              </div>

              <Button 
                onClick={() => setStep(5)}
                className="w-full bg-[#2563EB] hover:bg-[#1d4ed8] text-white py-6 text-lg font-semibold rounded-xl"
              >
                Test a Price Change
                <Sliders className="ml-2 h-5 w-5" />
              </Button>
            </div>
          )}

          {/* STEP 5: Scenario Simulation */}
          {step === 5 && selectedProduct && (
            <div>
              <button 
                onClick={() => setStep(4)}
                className="flex items-center gap-2 text-[#94A3B8] hover:text-[#F8FAFC] mb-6 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to product
              </button>

              <h2 className="text-2xl font-bold text-[#F8FAFC] mb-2">
                Test a Price Change
              </h2>
              <p className="text-[#94A3B8] mb-8">
                See what happens before you decide. Simulation only — live prices unchanged.
              </p>

              {/* Product Reference */}
              <div className="bg-[#111827]/50 border border-[#2563EB]/10 rounded-xl p-4 mb-6">
                <p className="text-sm text-[#94A3B8]">{selectedProduct.name}</p>
                <p className="text-[#F8FAFC] font-medium">Current: ${selectedProduct.price}</p>
              </div>

              {/* Price Slider */}
              <div className="bg-[#111827] border border-[#2563EB]/20 rounded-2xl p-6 mb-6">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-sm text-[#94A3B8]">New Price</span>
                  <span className="text-2xl font-bold text-[#2563EB]">${simulatedPrice?.toFixed(2)}</span>
                </div>
                
                <input
                  type="range"
                  min={selectedProduct.price * 0.7}
                  max={selectedProduct.price * 1.3}
                  step={0.01}
                  value={simulatedPrice}
                  onChange={(e) => setSimulatedPrice(parseFloat(e.target.value))}
                  className="w-full h-2 bg-[#2563EB]/20 rounded-full appearance-none cursor-pointer
                    [&::-webkit-slider-thumb]:appearance-none
                    [&::-webkit-slider-thumb]:w-5
                    [&::-webkit-slider-thumb]:h-5
                    [&::-webkit-slider-thumb]:bg-[#2563EB]
                    [&::-webkit-slider-thumb]:rounded-full
                    [&::-webkit-slider-thumb]:cursor-pointer"
                />
                
                <div className="flex justify-between text-xs text-[#94A3B8] mt-2">
                  <span>-30%</span>
                  <span>Current</span>
                  <span>+30%</span>
                </div>
              </div>

              {/* Simulation Results */}
              {simulatedPrice !== selectedProduct.price && (
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className={`rounded-xl p-5 ${
                    parseFloat(calculateSimulation(simulatedPrice).demandChange) < 0 
                      ? 'bg-red-500/10 border border-red-500/30' 
                      : 'bg-green-500/10 border border-green-500/30'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      {parseFloat(calculateSimulation(simulatedPrice).demandChange) < 0 
                        ? <TrendingDown className="w-5 h-5 text-red-400" />
                        : <TrendingUp className="w-5 h-5 text-green-400" />
                      }
                      <span className="text-sm text-[#94A3B8]">Demand</span>
                    </div>
                    <p className={`text-2xl font-bold ${
                      parseFloat(calculateSimulation(simulatedPrice).demandChange) < 0 
                        ? 'text-red-400' 
                        : 'text-green-400'
                    }`}>
                      {calculateSimulation(simulatedPrice).demandChange}%
                    </p>
                  </div>
                  
                  <div className={`rounded-xl p-5 ${
                    parseFloat(calculateSimulation(simulatedPrice).profitChange) < 0 
                      ? 'bg-red-500/10 border border-red-500/30' 
                      : 'bg-green-500/10 border border-green-500/30'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      {parseFloat(calculateSimulation(simulatedPrice).profitChange) < 0 
                        ? <TrendingDown className="w-5 h-5 text-red-400" />
                        : <TrendingUp className="w-5 h-5 text-green-400" />
                      }
                      <span className="text-sm text-[#94A3B8]">Profit</span>
                    </div>
                    <p className={`text-2xl font-bold ${
                      parseFloat(calculateSimulation(simulatedPrice).profitChange) < 0 
                        ? 'text-red-400' 
                        : 'text-green-400'
                    }`}>
                      {calculateSimulation(simulatedPrice).profitChange}%
                    </p>
                  </div>
                </div>
              )}

              {/* Safety Reminder */}
              <div className="bg-[#2563EB]/5 border border-[#2563EB]/20 rounded-xl p-4 mb-8">
                <p className="text-sm text-[#94A3B8] flex items-center gap-2">
                  <Lock className="w-4 h-4 text-[#2563EB]" />
                  Simulation only — your live prices remain unchanged
                </p>
              </div>

              <Button 
                onClick={() => setStep(6)}
                className="w-full bg-[#2563EB] hover:bg-[#1d4ed8] text-white py-6 text-lg font-semibold rounded-xl"
              >
                See Full Summary
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          )}

          {/* STEP 6: PLG Conversion Bridge */}
          {step === 6 && (
            <div>
              <button 
                onClick={() => setStep(3)}
                className="flex items-center gap-2 text-[#94A3B8] hover:text-[#F8FAFC] mb-6 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>

              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/30 rounded-full px-6 py-2 mb-4">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span className="text-green-400 font-medium text-sm">Analysis Complete</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-[#F8FAFC] mb-2">
                  Your Pricing Summary
                </h2>
                <p className="text-[#94A3B8]">
                  Here's what we found — and what you can do next.
                </p>
              </div>

              {/* Key Findings */}
              <div className="space-y-4 mb-8">
                {/* Risk Product */}
                {analyzedProducts.find(p => p.risk === 'risk') && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-5">
                    <div className="flex items-start gap-3">
                      <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-[#F8FAFC] mb-1">At Risk</p>
                        <p className="text-sm text-[#94A3B8]">
                          <strong>{analyzedProducts.find(p => p.risk === 'risk')?.name}</strong> — {analyzedProducts.find(p => p.risk === 'risk')?.riskReason}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Safe Product */}
                {analyzedProducts.find(p => p.risk === 'safe') && (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-5">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-[#F8FAFC] mb-1">Safe</p>
                        <p className="text-sm text-[#94A3B8]">
                          <strong>{analyzedProducts.find(p => p.risk === 'safe')?.name}</strong> — {analyzedProducts.find(p => p.risk === 'safe')?.riskReason}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Next Step */}
                <div className="bg-[#2563EB]/10 border border-[#2563EB]/30 rounded-xl p-5">
                  <div className="flex items-start gap-3">
                    <Activity className="w-5 h-5 text-[#2563EB] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-[#F8FAFC] mb-1">Recommended Next Step</p>
                      <p className="text-sm text-[#94A3B8]">
                        Review the {analyzedProducts.filter(p => p.risk !== 'safe').length} products flagged for attention. Start with the highest-risk items and test scenarios before making changes.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA Options */}
              <div className="space-y-3">
                <Button 
                  onClick={() => setShowEmailModal(true)}
                  className="w-full bg-[#2563EB] hover:bg-[#1d4ed8] text-white py-6 text-lg font-semibold rounded-xl"
                >
                  <Mail className="mr-2 h-5 w-5" />
                  Email Me This Analysis
                </Button>
                
                <Button 
                  onClick={() => { setStep(3); setShowAllProducts(false); }}
                  variant="outline" 
                  className="w-full border-[#2563EB]/50 text-[#F8FAFC] hover:bg-[#2563EB]/10 py-6 text-lg font-semibold rounded-xl"
                >
                  <RefreshCw className="mr-2 h-5 w-5" />
                  Test More Scenarios
                </Button>
                
                <Link 
                  to="/coming-soon"
                  className="block"
                >
                  <Button 
                    variant="ghost" 
                    className="w-full text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#111827] py-6 text-lg font-semibold rounded-xl"
                  >
                    Get Started with Full Product →
                  </Button>
                </Link>
              </div>

              {/* Final Reassurance */}
              <p className="text-center text-sm text-[#94A3B8] mt-8">
                Your live prices were not changed. Continue when you're ready.
              </p>
            </div>
          )}

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PricingHealthCheck;
